import React, { useState } from 'react';
import { usePWAInstall } from '../hooks/usePWAInstall';
import { useTranslation } from 'react-i18next';

export const InstallPWAPrompt: React.FC = () => {
    const { t } = useTranslation();
    const { isInstallable, isIOS, isStandalone, promptInstall } = usePWAInstall();
    const [isVisible, setIsVisible] = useState(false);

    React.useEffect(() => {
        const handleShow = () => {
            setIsVisible(true);
        };
        window.addEventListener('aura-show-pwa-prompt', handleShow);
        return () => window.removeEventListener('aura-show-pwa-prompt', handleShow);
    }, []);

    // Hide if already installed or user explicitly dismissed it
    // Solo ocultamos si ya está instalada o rechazada.
    // Si no es installable (dev mode) mostraremos unas instrucciones genéricas.
    if (isStandalone || !isVisible) return null;

    const handleDismiss = () => {
        setIsVisible(false);
        
    };

    return (
        <div className="fixed inset-0 z-[100] p-4 flex items-center justify-center bg-black/60 backdrop-blur-sm animate-in fade-in duration-300">
            <div className="bg-[#121212] border border-[#D4AF37]/30 rounded-[2rem] p-6 shadow-[0_15px_50px_rgba(0,0,0,0.9)] w-full max-w-sm relative animate-in zoom-in-95 duration-300">
                
                <button 
                    onClick={handleDismiss} 
                    className="absolute top-4 right-4 text-white/40 hover:text-white bg-black/50 rounded-full h-7 w-7 flex items-center justify-center transition-colors"
                    aria-label={t('common.close', 'Cerrar')}
                >
                    <span className="material-symbols-outlined text-sm font-bold">close</span>
                </button>

                <div className="flex items-start gap-4 pr-6">
                    <div className="bg-gradient-to-br from-[#D4AF37]/20 to-black border border-[#D4AF37]/50 rounded-[18px] w-14 h-14 flex items-center justify-center flex-shrink-0 shadow-[0_0_20px_rgba(212,175,55,0.15)] flex-col overflow-hidden">
                        <img src="/icon-192x192.png" alt="Aura Method Logo" className="w-full h-full object-cover" />
                    </div>
                    
                    <div className="pt-1">
                        <h4 className="text-white font-black text-sm uppercase tracking-widest mb-1.5 flex items-center gap-1.5">
                            {t('pwa.install_prompt.title')}
                        </h4>
                        <p className="text-white/60 text-[11px] leading-relaxed mb-5 pr-2 font-medium">
                            {t('pwa.install_prompt.desc')}
                        </p>
                        
                        {isInstallable ? (
                            <button 
                                onClick={promptInstall}
                                className="w-full premium-gold-gradient text-black font-black uppercase tracking-widest text-[10px] py-3.5 rounded-xl hover:scale-[1.02] active:scale-95 transition-all shadow-[0_5px_15px_rgba(212,175,55,0.3)]"
                            >
                                {t('pwa.install_prompt.btn_install')}
                            </button>
                        ) : isIOS ? (
                            <div className="bg-black/40 rounded-xl p-3 border border-white/5 space-y-3">
                                <p className="text-[9px] text-[#D4AF37] font-black uppercase tracking-[0.2em] text-center mb-1">{t('settings.actions.install_ios_instruction').split(':')[0]}</p>
                                <div className="flex items-center gap-3 text-[11px] text-white/80 font-medium">
                                    <div className="bg-blue-500/10 text-blue-400 h-6 w-6 rounded-md flex items-center justify-center flex-shrink-0">
                                        <span className="material-symbols-outlined text-sm font-normal">ios_share</span>
                                    </div>
                                    <span dangerouslySetInnerHTML={{ __html: t('pwa.install_prompt.ios_step1') }} />
                                </div>
                                <div className="flex items-center gap-3 text-[11px] text-white/80 font-medium">
                                    <div className="bg-white/10 text-white h-6 w-6 rounded-md flex items-center justify-center flex-shrink-0">
                                        <span className="material-symbols-outlined text-sm font-normal">swipe_up</span>
                                    </div>
                                    <span dangerouslySetInnerHTML={{ __html: t('pwa.install_prompt.ios_step2') }} />
                                </div>
                                <div className="flex items-center gap-3 text-[11px] text-white/80 font-medium">
                                    <div className="bg-white/10 text-white h-6 w-6 rounded-md flex items-center justify-center flex-shrink-0">
                                        <span className="material-symbols-outlined text-sm font-normal">add_box</span>
                                    </div>
                                    <span dangerouslySetInnerHTML={{ __html: t('pwa.install_prompt.ios_step3') }} />
                                </div>
                                <div className="flex items-center gap-3 text-[11px] text-white/80 font-medium">
                                    <div className="bg-green-500/10 text-green-400 h-6 w-6 rounded-md flex items-center justify-center flex-shrink-0">
                                        <span className="material-symbols-outlined text-sm font-normal">check_circle</span>
                                    </div>
                                    <span dangerouslySetInnerHTML={{ __html: t('pwa.install_prompt.ios_step4') }} />
                                </div>
                            </div>
                        ) : (
                            <div className="bg-black/40 rounded-xl p-3 border border-white/5 text-center mt-3">
                                <p className="text-[10px] text-white/70 font-medium" dangerouslySetInnerHTML={{ __html: t('settings.actions.install_dev_instruction') }} />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};
